import {
  createUnifiedTheme,
  genPageTheme,
  PageTheme,
  shapes,
} from '@backstage/theme';

const TOP_BAR_HEIGHT = 56;
// Matches the content panel's corner radius so sidebar items start on its straight edge.
const PANEL_RADIUS = 16;

// Page headers render as a white strip inside the content panel instead of a
// coloured banner. Colours/shape are kept so anything reading them still works.
const headerPageTheme = (
  options: Parameters<typeof genPageTheme>[0],
): PageTheme => ({
  ...genPageTheme(options),
  backgroundImage: 'none',
  fontColor: '#14213D',
});

export const capgeminiTheme = createUnifiedTheme({
  palette: {
    type: 'light',
    mode: 'light',
    background: {
      default: '#F8F8F8',
      paper: '#FFFFFF',
    },
    status: {
      ok: '#1DB954',
      warning: '#FF9800',
      error: '#E22134',
      running: '#0058AB',
      pending: '#FEB100',
      aborted: '#757575',
    },
    bursts: {
      fontColor: '#FEFEFE',
      slackChannelText: '#ddd',
      backgroundColor: { default: '#0058AB' },
      gradient: {
        linear: 'linear-gradient(-137deg, #1DB8F2 0%, #0058AB 100%)',
      },
    },
    primary: {
      main: '#0058AB',
    },
    secondary: {
      main: '#1DB8F2',
    },
    banner: {
      info: '#0058AB',
      error: '#E22134',
      text: '#FFFFFF',
      link: '#000000',
      closeButtonColor: '#FFFFFF',
      warning: '#FF9800',
    },
    border: '#E6E6E6',
    textContrast: '#000000',
    textVerySubtle: '#DDD',
    textSubtle: '#6E6E6E',
    highlight: '#FFFBCC',
    errorBackground: '#FFEBEE',
    warningBackground: '#F59B23',
    infoBackground: '#EBF5FF',
    errorText: '#CA001B',
    infoText: '#004E8A',
    warningText: '#000000',
    linkHover: '#1DB8F2',
    link: '#0058AB',
    gold: '#FEB100',
    navigation: {
      background: '#121A38',
      indicator: '#1DB8F2',
      color: '#B5B5B5',
      selectedColor: '#FFFFFF',
      navItem: {
        hoverBackground: '#0058AB',
      },
      submenu: {
        background: '#0D1429',
      },
    },
    pinSidebarButton: {
      icon: '#FFFFFF',
      background: '#0058AB',
    },
    tabbar: {
      indicator: '#1DB8F2',
    },
  },
  defaultPageTheme: 'home',
  pageTheme: {
    home: headerPageTheme({ colors: ['#0058AB', '#121A38'], shape: shapes.wave }),
    documentation: headerPageTheme({ colors: ['#00828E', '#121A38'], shape: shapes.wave2 }),
    tool: headerPageTheme({ colors: ['#71609E', '#121A38'], shape: shapes.round }),
    service: headerPageTheme({ colors: ['#0058AB', '#1DB8F2'], shape: shapes.wave }),
    website: headerPageTheme({ colors: ['#121A38', '#0058AB'], shape: shapes.wave }),
    library: headerPageTheme({ colors: ['#8F3237', '#121A38'], shape: shapes.wave }),
    other: headerPageTheme({ colors: ['#121A38', '#0058AB'], shape: shapes.wave }),
    app: headerPageTheme({ colors: ['#BE4D00', '#121A38'], shape: shapes.wave }),
    apis: headerPageTheme({ colors: ['#00828E', '#121A38'], shape: shapes.wave2 }),
    card: headerPageTheme({ colors: ['#00D5D0', '#0058AB'], shape: shapes.wave }),
  },
  components: {
    BackstageSidebar: {
      styleOverrides: { drawer: { top: TOP_BAR_HEIGHT + PANEL_RADIUS } },
    },
    BackstageSidebarPage: {
      styleOverrides: { root: { background: 'transparent' } },
    },
    // Pages are 100vh by default; make them fill the content panel instead.
    BackstagePage: { styleOverrides: { root: { height: '100%' } } },
    BackstageHeader: {
      styleOverrides: {
        header: {
          backgroundColor: '#FFFFFF',
          boxShadow: 'none',
          borderBottom: `1px solid #E6E6E6`,
        },
      },
    },
    // Cards share the shell's 16px radius with a soft navy-tinted shadow.
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          border: '1px solid #E6E8F0',
          boxShadow: '0 1px 2px rgba(18,26,56,0.04), 0 10px 24px -14px rgba(18,26,56,0.22)',
        },
      },
    },
    // Larger corner radius on the search modal to match the rest of the shell.
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 16,
          overflow: 'hidden',
        },
      },
    },
  },
});
